// ./src/components/features/CreateEventWizard/wizardSetup.ts
//
// What each wizard step WRITES once the event exists (ROADMAP A2b #62 P6a). Owner ruling 2026-09-29: the event is
// created as a draft the moment the essentials are in — type, name, date/time, where, and how guests join — and every
// later step adds to the real event, so the host can finish from any step and nothing is lost to a reload.
//
// ⚠ Pure — `npm run check-event-setup` runs these on fixtures. The shell (CreateEventWizard) only sequences them.
import type { EventInput, EventSettings, EventSetupType, TicketTypeInput } from '@/utils/api';
import { featuresAfterRules } from '@/utils/eventFeatureRules';
import type { EventSetupRule } from '@/config/eventSetup.generated';

import { draftToEventInput } from './draftToEventInput';
import { EVENT_FORMAT, EVENT_MODE, GUEST_TRACKING, QUICK_KIND, SCHEDULE_MODE, headcountPartyCap, quickKindOf } from './wizardTypes';
import type { EventMode, EventWizardDraft, QuickKind, WizardStepKey } from './wizardTypes';

const COVER_KEYS = ['coverUploadedFileId', 'coverUrl', 'coverVideoUploadedFileId', 'coverVideoUrl'] as const;

/**
 * The essentials, for the CREATE and for re-saving them when the host goes back and changes one.
 * ⚠ No cover fields — the Media step owns those, and sending them from here would blank a cover the host set there
 *   and then went back to fix the title. ⚠ No `eventStatus` on an UPDATE: only the create says "draft".
 */
export const essentialsInput = (draft: EventWizardDraft, forCreate: boolean): EventInput => {
  const input: Record<string, unknown> = { ...draftToEventInput(draft) };

  COVER_KEYS.forEach((key) => {
    delete input[key];
  });

  if (!forCreate) {
    delete input.eventStatus;
  }

  return input as EventInput;
};

/** The Media step's fields only. */
export const mediaInput = (draft: EventWizardDraft): EventInput => {
  const all = draftToEventInput(draft) as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  COVER_KEYS.forEach((key) => {
    out[key] = all[key] ?? null;
  });

  return out as EventInput;
};

/**
 * The organizer page a NEW event starts on in the wizard (#62 P6(d2), owner 2026-09-30: "automatically select organizer's
 * profile if any, and let user remove it or change to another one") — the SERVER's own rule (`autoOrganizerProfileId`):
 * the account's only page that is not a draft, else null.
 * ⚠ With two or more, nothing is guessed: the wrong one puts the event on another public page, visible to strangers
 *   before the host notices. The picker is on screen either way, so the host sees and changes the choice.
 * ⚠ A DRAFT page (listingStatus 'draft', ORGANIZER_LISTING_STATUS.DRAFT) is never picked — `/o/<slug>` does not serve it.
 */
export const wizardOrganizerDefault = (profiles: ReadonlyArray<{ id?: string | null; listingStatus?: string | null }>): string | null => {
  const candidates = profiles.filter((p) => !!p.id && p.listingStatus !== 'draft');

  return candidates.length === 1 ? String(candidates[0].id) : null;
};

// What is SAVED for a host's ticks lives in `utils/eventFeatureRules` (shared with the Features page and the menu).
export { featuresAfterRules };

/**
 * The features an event STARTS with: what its type suggests, run through the rules (a ticketed event gains Tickets; a
 * save-the-date loses what hangs off a reply). This is what the event gets if the host finishes before the Features
 * step — never "everything", which would defeat the tidy menu, and never nothing.
 */
export const initialFeatures = (
  type: EventSetupType | null,
  parent: EventSetupType | null,
  rules: readonly EventSetupRule[],
  eventMode: string | null,
): string[] => {
  const suggested = (type?.offers ?? []).filter((o) => o.strength === 'suggested').map((o) => o.featureCode);

  return featuresAfterRules(type, parent, rules, eventMode, suggested);
};

/**
 * The highlight links to add and remove to make the event carry exactly `wanted`. `links` maps a highlight id to the
 * id of the link row the wizard created for it, so a highlight un-ticked after saving is actually unlinked.
 */
export const highlightChanges = (
  wanted: readonly string[],
  links: Readonly<Record<string, string>>,
): { add: string[]; remove: Array<{ highlightId: string; linkId: string }> } => ({
  add: wanted.filter((id) => !links[id]),
  remove: Object.entries(links)
    .filter(([highlightId]) => !wanted.includes(highlightId))
    .map(([highlightId, linkId]) => ({ highlightId, linkId })),
});

/**
 * The event's settings with `features` replaced — every other key kept. ⚠ `settings` is ONE jsonb column and an update
 * REPLACES it, so writing `{ features }` alone would wipe the hero, payments, check-in and coupon settings.
 */
export const withFeatures = (existing: EventSettings | null | undefined, on: readonly string[]): EventSettings => ({
  ...(existing ?? {}),
  features: { on: [...on] },
});

/**
 * #139 — what the QUICK headcount path fixes on the draft: one date, an open RSVP that counts heads (up to
 * `headcountPartyCap` per reply), and none of the Guests step's options.
 * ⚠ In person, and no subtitle: the quick screens ask neither, and an "Online" format with no link (an account default,
 *   or a choice made before switching) published a page saying "Online event" with nowhere to join (review, #139).
 */
const quickHeadcountPatch = (draft: EventWizardDraft): Partial<EventWizardDraft> => ({
  quick: true,
  scheduleMode: SCHEDULE_MODE.SINGLE,
  eventFormat: EVENT_FORMAT.IN_PERSON,
  subtitle: '',
  eventMode: EVENT_MODE.LINK_RSVP,
  guestTracking: GUEST_TRACKING.HEADCOUNT,
  rsvpGroupSize: headcountPartyCap(draft.rsvpGroupSize),
  guestsNeedApproval: false,
  capacity: null,
  capacityFromVenueId: null,
  enableWaitlist: false,
  rsvpDeadline: null,
  rsvpDeadlineDaysBefore: null,
});

/**
 * #148 — what the quick TICKET SALE fixes: one date, ticketed, counted by headcount (the buyer plus the tickets, #134),
 * no limit per order, approval, waitlist or deadline, no subtitle.
 * ⚠ NOT the place, the format or the capacity: its Where step asks the first two, and its ticket screen asks how many
 *   (the capacity) — overwriting them here would blank what the host is about to be asked for.
 */
const quickTicketsPatch: Partial<EventWizardDraft> = {
  quick: true,
  scheduleMode: SCHEDULE_MODE.SINGLE,
  subtitle: '',
  eventMode: EVENT_MODE.TICKETED,
  guestTracking: GUEST_TRACKING.HEADCOUNT,
  maxTicketsPerOrder: null,
  guestsNeedApproval: false,
  enableWaitlist: false,
  rsvpDeadline: null,
  rsvpDeadlineDaysBefore: null,
};

/**
 * What a QUICK path fixes on the draft (`kind`: the headcount, #139, or the ticket sale, #148). Applied when the host
 * takes the path (by `startQuick`, which keeps what it overwrote) and again by `quickEventInput`.
 */
export const quickDraftPatch = (draft: EventWizardDraft, kind: QuickKind): Partial<EventWizardDraft> =>
  kind === QUICK_KIND.TICKETS ? { ...quickTicketsPatch } : quickHeadcountPatch(draft);

const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * #139 — taking the quick path: its patch, the type kept (else `fallbackType`, "Other"), and what it overwrote.
 * ⚠ The overwritten values are KEPT (`quickRestore`) — the card promises "tap again to use the full setup instead", and
 *   without them that tap left a host who had set Tickets, names, a capacity and a deadline with an open headcount.
 */
export const startQuick = (
  draft: EventWizardDraft,
  fallbackType: { id: string; code: string; categoryGroup?: string | null } | null,
  kind: QuickKind,
): Partial<EventWizardDraft> => {
  const { quick: _quick, ...fixed } = quickDraftPatch(draft, kind);
  const typeFields: Partial<EventWizardDraft> =
    !draft.eventCategoryId && fallbackType
      ? {
          eventCategoryId: fallbackType.id,
          eventCategoryCode: fallbackType.code,
          eventCategoryGroup: (fallbackType.categoryGroup ?? null) as EventWizardDraft['eventCategoryGroup'],
        }
      : {};
  const set: Partial<EventWizardDraft> = { ...fixed, ...typeFields };
  const before = Object.fromEntries(Object.keys(set).map((key) => [key, draft[key as keyof EventWizardDraft] ?? null])) as Partial<EventWizardDraft>;

  return { ...set, quick: true, quickKind: kind, quickRestore: { before, set } };
};

/**
 * #139 — leaving the quick path: every field it overwrote comes back, unless the host has changed it since (a type picked
 * while the path was on stays picked).
 */
export const leaveQuick = (draft: EventWizardDraft): Partial<EventWizardDraft> => {
  const restore = draft.quickRestore;
  const back: Partial<EventWizardDraft> = {};

  if (restore) {
    (Object.keys(restore.before) as Array<keyof EventWizardDraft>).forEach((key) => {
      if (sameValue(draft[key], restore.set[key])) {
        (back as Record<string, unknown>)[key] = restore.before[key];
      }
    });
  }

  return { ...back, quick: false, quickKind: null, quickRestore: null };
};

/**
 * #148 — a tap on one of the Type step's two quick cards: the path it names is taken, or — tapped again — left.
 * ⚠ Moving from one quick path to the other LEAVES the first before taking the second, so what the second keeps to give
 *   back is the host's FULL-setup answers, not the first quick path's (which would then be "restored" by a later tap).
 */
export const toggleQuick = (
  draft: EventWizardDraft,
  fallbackType: { id: string; code: string; categoryGroup?: string | null } | null,
  kind: QuickKind,
): Partial<EventWizardDraft> => {
  const current = quickKindOf(draft);

  if (current === kind) {
    return leaveQuick(draft);
  }

  if (!current) {
    return startQuick(draft, fallbackType, kind);
  }

  const back = leaveQuick(draft);

  return { ...back, ...startQuick({ ...draft, ...back }, fallbackType, kind) };
};

/**
 * #139 — the quick event's CREATE (or, after a failed publish was retried, its update): the essentials with the quick
 * path's fixed answers, plus the picture.
 * ⚠ Re-applies `quickDraftPatch` rather than trusting the draft: a restored or half-switched draft may still hold a
 *   repeat rule or a capacity the quick screens never showed, and a quick event carries nothing the host did not see.
 * ⚠ `venueId` and `onlineMeetingUrl` are sent as an explicit NULL — absent would let the server stamp the account's
 *   default venue onto an event whose screens never mentioned a place (`EventCore.applyAccountDefaults`).
 * ⚠ The picture only — no video (the quick Media step offers none). The create is a DRAFT; Finish publishes it next.
 */
export const quickEventInput = (draft: EventWizardDraft, forCreate: boolean): EventInput => {
  const kind = quickKindOf(draft) ?? QUICK_KIND.HEADCOUNT;
  const quick = { ...draft, ...quickDraftPatch(draft, kind), coverVideo: null };
  // #148 — the ticket sale's place is the one its Where step asked (`draftToEventInput` sends an explicit null for the
  // half the format does not use), and its currency is the one its ticket is priced in.
  const input: Record<string, unknown> =
    kind === QUICK_KIND.TICKETS
      ? { ...draftToEventInput(quick), ...(draft.quickTicket?.currencyCode ? { currencyCode: draft.quickTicket.currencyCode } : {}) }
      : { ...draftToEventInput(quick), venueId: null, onlineMeetingUrl: null };

  if (!forCreate) {
    delete input.eventStatus;
  }

  return input as EventInput;
};

/**
 * #139 — a quick event's features: only what the rules REQUIRE of its mode (an open RSVP; #148 a ticketed event, which
 * gains Tickets). Never the type's suggestions — "it collects a headcount and nothing else" (owner), and every feature
 * adds to the event's menu and its RSVP form.
 */
export const quickFeatures = (
  type: EventSetupType | null,
  parent: EventSetupType | null,
  rules: readonly EventSetupRule[],
  eventMode: EventMode,
): string[] => featuresAfterRules(type, parent, rules, eventMode, []);

/** The guest mode a quick path makes its event in. */
export const quickEventMode = (kind: QuickKind): EventMode => (kind === QUICK_KIND.TICKETS ? EVENT_MODE.TICKETED : EVENT_MODE.LINK_RSVP);

/** #148 — what the quick ticket screen still needs before Next, by the input it belongs to. PRESENCE only. */
export type QuickTicketField = 'name' | 'price' | 'currencyCode' | 'capacity';

export const quickTicketMissing = (draft: Pick<EventWizardDraft, 'quickTicket' | 'capacity'>): QuickTicketField[] => {
  const ticket = draft.quickTicket;
  const missing: QuickTicketField[] = [];

  if (!ticket?.name.trim()) {
    missing.push('name');
  }

  if (ticket?.price == null) {
    missing.push('price');
  }

  if (!ticket?.currencyCode) {
    missing.push('currencyCode');
  }

  if (draft.capacity == null) {
    missing.push('capacity');
  }

  return missing;
};

/**
 * #148 — the ticket type Finish makes (or, retried, updates): the name and the price. ⚠ No quantity of its own — the
 * event's capacity is the "how many", so a second ticket type added later shares those places rather than adding more.
 * ⚠ No currency: a ticket is priced in its EVENT's (A4.7a), which `quickEventInput` sets.
 */
export const quickTicketInput = (draft: Pick<EventWizardDraft, 'quickTicket'>): TicketTypeInput => ({
  name: (draft.quickTicket?.name ?? '').trim(),
  price: draft.quickTicket?.price ?? 0,
});

/** #148 — why a quick ticket sale is KEPT as a draft on Finish instead of published. */
export const QUICK_PUBLISH_HOLD = {
  /** Its ticket costs money and card payments are not set up: nobody could pay. */
  CARDS: 'cards',
  /** Its ticket costs money and whether card payments are set up could not be checked. */
  UNKNOWN: 'unknown',
} as const;

export type QuickPublishHold = (typeof QUICK_PUBLISH_HOLD)[keyof typeof QUICK_PUBLISH_HOLD];

/**
 * #148 — whether Finish may PUBLISH (null) or keeps the event a draft, and why. The owner: "publish only when it can
 * sell". A free ticket sells without card payments; a paid one only when Stripe answered that charges are enabled.
 * ⚠ `connect` null = the check FAILED (or has not answered): that is not "ready", so it holds — and says it could not
 *   check, never "not set up" (it may be).
 * ⚠ Only the ticket sale is ever held: the headcount path takes no money.
 */
export const quickPublishHold = (
  draft: Pick<EventWizardDraft, 'quick' | 'quickKind' | 'quickTicket'>,
  connect: { chargesEnabled?: boolean | null } | null,
): QuickPublishHold | null => {
  if (quickKindOf(draft) !== QUICK_KIND.TICKETS || (draft.quickTicket?.price ?? 0) <= 0) {
    return null;
  }

  if (!connect) {
    return QUICK_PUBLISH_HOLD.UNKNOWN;
  }

  return connect.chargesEnabled === true ? null : QUICK_PUBLISH_HOLD.CARDS;
};

/** #148 — a priced quick sale whose card-payments check has not answered yet: Finish waits for it. */
export const QUICK_FINISH_CHECKING = 'checking';

export type QuickFinishState = QuickPublishHold | typeof QUICK_FINISH_CHECKING | null;

/**
 * #148 — what Finish will do, as the screens SAY it (its button, the picture step's card): publish (null), keep a draft
 * and why, or — a priced sale whose check has not answered — nothing claimed yet (`checking`). ⚠ The one source for every
 * place that says it, so the button and the card cannot disagree; Finish compares its own fresh answer with it.
 */
export const quickFinishState = (
  draft: Pick<EventWizardDraft, 'quick' | 'quickKind' | 'quickTicket'>,
  payments: { checked: boolean; status: { chargesEnabled?: boolean | null } | null },
): QuickFinishState => {
  const priced = quickKindOf(draft) === QUICK_KIND.TICKETS && (draft.quickTicket?.price ?? 0) > 0;

  if (priced && !payments.checked) {
    return QUICK_FINISH_CHECKING;
  }

  return quickPublishHold(draft, payments.checked ? payments.status : null);
};

/** #148 — the quick step that asks each input the server may name in a refusal of the quick Finish. */
const QUICK_FIELD_STEP: Readonly<Record<string, WizardStepKey>> = {
  eventCategoryId: 'type',
  title: 'basics',
  startsAt: 'basics',
  endsAt: 'basics',
  isAllDay: 'basics',
  timezone: 'basics',
  eventFormat: 'location',
  venueId: 'location',
  onlineMeetingUrl: 'location',
  capacity: 'tickets',
  currencyCode: 'tickets',
  name: 'tickets',
  price: 'tickets',
};

/**
 * #148 — the step to send the host to after a refused quick Finish: the first of `steps` asking an input the refusal
 * names, else null (it stays where it is, the reason at the top). ⓘ Reads the errors as sent — a name the quick path does
 * not ask is skipped, not guessed.
 */
export const quickStepOfRefusal = (result: { errors?: unknown } | null | undefined, steps: readonly WizardStepKey[]): WizardStepKey | null => {
  const items = Array.isArray(result?.errors) ? (result?.errors as Array<{ name?: string | null }>) : [];
  const named = items.map((item) => QUICK_FIELD_STEP[item?.name ?? '']).filter((key): key is WizardStepKey => !!key && steps.includes(key));

  return steps.find((key) => named.includes(key)) ?? null;
};
